/**
 * Normalizes a value in preparation for sending it to analytics service. Note
 * that while this function could very likely be improved, any changes that affect
 * its output could result in a major loss of continuity in analytics data.
 * @param {any} value The value to be normalized.
 * @returns {string} The normalized value.
 */
const sanitizeValue = (value) => {
    let sanitizedValue = value;

    // If not a string or a number return the original unsanitized value.
    if (typeof sanitizedValue !== 'string' && typeof sanitizedValue !== 'number') {
        return sanitizedValue;
    }

    // Ensure that numeric decimal places are preserved as underscores.
    if (typeof sanitizedValue === 'number') {
        sanitizedValue = sanitizedValue.toString().replace('.', '_');
    }

    return (
        sanitizedValue
            .toString()
            .trim()
            // Remove shortcodes.
            .replace(/\[.+\]/g, '')
            // Replace breaks with underscores.
            .replace(/<(?:br|br |br\/|br \/)>/g, '_')
            // Remove all HTML tags.
            .replace(/(<([^>]+)>)/g, '')
            // Lowercase all characters.
            .toLowerCase()
            // Convert all dashes and spaces to underscores.
            .replace(/[-\s+]/g, '_')
            // Replace ampersand characters with 'and'.
            .replace(/(?:#038;)|&/g, 'and')
            // Remove any character but letters, numbers, and underscores.
            .replace(/[^a-z0-9_:]/g, '')
    );
};

/**
 * Attempts to send data to the analytics service.
 * @param {Object} dataObject object containing data to be send to analytics service.
 * @param {integer} timeout set timeout for the poller to wait.
 */
const sendAnalyticsData = (dataObject, timeout = 0) => {
    const refreshAttempts = 4;

    let retries = 0;

    const poller = () => {
        setTimeout(() => {
            // eslint-disable-next-line no-underscore-dangle
            if (window?._satellite !== undefined) {
                // Create a copy of the data object to avoid modifying the parameter
                const workingDataObject = { ...dataObject };

                // Ensure some form of page name is included in the linked data.
                if (!window.analytics_data?.page_name?.length) {
                    workingDataObject.page_name = sanitizeValue(document.title);
                }

                // Include the current guest's breakpoint in the linked data.
                const { page } = window.disneyRewards.globals;
                workingDataObject.breakpoint = page ? page.currentAnalyticsBreakpoint : 'unknown';

                // Add descriptor that this is not a page view if it is not set.
                if (workingDataObject?.page_view === undefined) {
                    workingDataObject.page_view = false;
                }

                // Add Chase login status if it is not set and if this is a page view.
                if (workingDataObject.page_view && workingDataObject?.login_status === undefined) {
                    const loginStatus = localStorage.getItem('oneid_logged_in');
                    workingDataObject.login_status = loginStatus === '1';
                }

                // Sanitize all linked data values, with exceptions.
                const keysToSkip = [
                    'account_type',
                    'cell',
                    'calculator_results',
                    'drrc_balance',
                    'drrc_number',
                    'page_url',
                    'pre_num_drrc',
                    'pre_reward_bal',
                    'pre_transfer_bal',
                    'product_string',
                    'purchase_id',
                    'reward_bal'
                ];
                const sanitizedDataObject = Object.entries(workingDataObject).reduce((workingObject, [key, value]) => {
                    return {
                        ...workingObject,
                        [key]: keysToSkip.includes(key) ? value : sanitizeValue(value)
                    };
                }, {});

                // eslint-disable-next-line no-underscore-dangle
                window._satellite.track('dr_analytics_data', sanitizedDataObject);
            } else {
                retries += 1;

                if (retries < refreshAttempts) {
                    poller();
                }
            }
        }, timeout);
    };

    poller();
};

/**
 * Initializes and enhances the digitalData object with client-side values.
 */
const initializeDigitalData = () => {
    if (!window.digitalData) {
        return;
    }

    // Populate responsive/breakpoint data
    const { page } = window.disneyRewards?.globals || {};
    const breakpoint = page ? page.currentAnalyticsBreakpoint : 'unknown';

    window.digitalData.page.attributes.responsive = breakpoint;
    window.digitalData.technical.breakpoint = breakpoint;

    // Populate login status from localStorage
    try {
        const loginStatus = localStorage.getItem('oneid_logged_in');
        window.digitalData.user.profile.loginStatus = loginStatus === '1';

        // Populate cell data from localStorage only if not already set from URL
        if (!window.digitalData.campaign.cell) {
            const cellData = localStorage.getItem('cell');
            if (cellData) {
                window.digitalData.campaign.cell = cellData;
            }
        }
    } catch (e) {
        // localStorage not available
    }

};

/**
 * Updates digitalData.event object with event-specific data.
 * @param {Object} eventData Event data to populate in digitalData.event
 */
const updateDigitalDataEvent = (eventData) => {
    if (!window.digitalData || !window.digitalData.event) {
        return;
    }

    // Clear previous event data
    window.digitalData.event = {
        name: eventData.event_name || '',
        module: eventData.module || '',
        linkName: eventData.navigation_link || eventData.footer_link || '',
        linkURL: eventData.link_url || '',
        linkType: eventData.link_type || '',
        contentLink: eventData.content_link || '',
        detail: eventData.detail || ''
    };
};

/**
 * Push events to Adobe Client Data Layer (ACDL).
 * ACDL uses an event-driven array pattern for tracking.
 * @param {Object} eventData - Event data object
 * @param {string} eventData.event - Event name (required)
 * @param {string} [eventData._trigger] - Optional Adobe Tags direct call rule name
 * @param {Object} [eventData.*] - Any other data to push
 */
const pushToDataLayer = window.pushToDataLayer || ((eventData) => {
    // Fallback implementation (shouldn't be needed if PHP version loads)
    if (!window.adobeDataLayer) {
        return;
    }
    if (!eventData.event) {
        return;
    }
    window.adobeDataLayer.push(eventData);
});

/**
 * Get OneID identity data from Disney OneID service if available.
 * @returns {Object} OneID identity data
 */
const getOneIdData = async () => {
    const oneIdData = {
        swid: '',
        unid: '',
        coreID: '',
        ecid: ''
    };

    // Try Disney OneID global object
    if (window.did) {
        try {
            const isLoggedIn = await window.did.getLoggedInStatus();
            const guest = isLoggedIn ? await window.did.getGuest() : null;
            if (guest) {
                oneIdData.swid = guest.profile?.swid || '';
                oneIdData.unid = guest.profile?.unid || '';
                oneIdData.coreID = guest.profile?.coreID || '';
            }
        } catch (e) {
            // OneID not available or failed, continue with fallbacks
        }
    }

    // Try Adobe Experience Cloud Visitor API for ECID
    if (window.Visitor && typeof window.Visitor.getInstance === 'function') {
        try {
            const visitor = window.Visitor.getInstance('@AdobeOrg');
            if (visitor && typeof visitor.getMarketingCloudVisitorID === 'function') {
                oneIdData.ecid = visitor.getMarketingCloudVisitorID() || '';
            }
        } catch (e) {
            // Visitor API not available yet
        }
    }

    try {
        // Try localStorage as fallback
        if (!oneIdData.swid) {
            oneIdData.swid = localStorage.getItem('oneID_swid') ||
                localStorage.getItem('disney_swid') || '';
        }
        if (!oneIdData.unid) {
            oneIdData.unid = localStorage.getItem('oneID_unid') ||
                localStorage.getItem('disney_unid') || '';
        }
    } catch (e) {
        // localStorage not available
    }


    return oneIdData;
};

/**
 * Track OneID authentication events.
 * Called when OneID login/registration completes.
 * @param {string} eventType - Type of event: 'login_initiation', 'login_complete', 'registration_start', 'registration_complete'
 * @param {Object} [additionalData] - Optional additional event data
 */
const trackOneIdEvent = async (eventType, additionalData = {}) => {
    // Map to Adobe Analytics events
    const eventMapping = {
        'login_initiation': 'OneID login initiated',      // event80
        'login_complete': 'OneID login completed',        // event82
        'registration_start': 'OneID registration started', // event83
        'registration_complete': 'OneID registration completed' // event84
    };

    const eventName = eventMapping[eventType] || 'OneID event';

    // Get fresh OneID data
    const oneIdData = await getOneIdData();

    // Push event with identity update
    pushToDataLayer({
        event: eventName,
        user: {
            profile: {
                loginStatus: !!oneIdData.swid,
                swid: oneIdData.swid,
                unid: oneIdData.unid,
                coreID: oneIdData.coreID,
                ecid: oneIdData.ecid
            }
        },
        interaction: {
            linkName: eventName,
            linkType: 'authentication',
            linkPosition: 'oneID',
            module: 'oneID-authentication'
        },
        ...additionalData
    });
};

export {
    sanitizeValue,
    sendAnalyticsData,
    initializeDigitalData,
    updateDigitalDataEvent,
    pushToDataLayer,
    getOneIdData,
    trackOneIdEvent
};
